import React from 'react';
import { usePlatformMode } from '../../../hooks/usePlatformMode';

const FeatureFlagsDebug: React.FC = () => {
  const { mode: platformMode, loading, error } = usePlatformMode();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading platform mode...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Error Loading Platform Mode
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            🚩 Platform Mode Debug
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Display of current platform mode and feature flags configuration
          </p>
        </div>

        {/* Platform Mode */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow-lg p-6 mb-8 border border-blue-200 dark:border-blue-800">
          <h2 className="text-xl font-semibold text-blue-900 dark:text-blue-100 mb-4">
            🎯 Current Platform Mode
          </h2>
          <div className="flex items-center gap-4">
            <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">
              {platformMode}
            </div>
            <div className="text-sm text-blue-600 dark:text-blue-400">
              Fetched from backend API
            </div>
          </div>
        </div>

        {/* Platform Mode Info */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            🎯 Platform Mode Information
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Current Mode</h3>
              <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{platformMode}</p>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Available Modes</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  <span className="text-gray-700 dark:text-gray-300"><strong>full</strong> - All features enabled</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                  <span className="text-gray-700 dark:text-gray-300"><strong>api_as_service</strong> - Only API as Service</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Environment Info */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            📋 Environment Information
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="font-medium text-gray-700 dark:text-gray-300">Platform Mode:</span>
              <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm">
                {platformMode}
              </code>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="font-medium text-gray-700 dark:text-gray-300">Source:</span>
              <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm">
                Backend API
              </code>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="font-medium text-gray-700 dark:text-gray-300">Build Time:</span>
              <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm">
                {new Date().toLocaleString()}
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureFlagsDebug;